import React from 'react';
import PropTypes from 'prop-types';
import { themes, ThemeContext, getTheme } from '@ids/theme';
import { styleOverrideReason } from '@ids/core';
import {
  Wrapper,
  CircularSpinnerOuter,
  CircularSpinnerInner,
  SVG
} from './StyledIndeterminateShort';

const { string, number, bool, oneOf } = PropTypes;

/** Spinner component */
export default class IndeterminateShort extends React.Component {
  static contextType = ThemeContext;
  static propTypes = {
    /**
     * Test automation ID.
     */
    automationId: string,
    /**
     * Whether to invert the colors in the theme
     */
    // eslint-disable-next-line react/boolean-prop-naming
    inverse: bool,
    /**
     * Size between 24 and 64 (px)
     */
    size: number,
    /**
     * When there is an application base theme defined, use this prop to override
     * (e.g. 'mint', 'proconnect', 'quickbooks', 'turbotax')
     */
    theme: oneOf(themes),
    /**
     * Fill color for svg when it is not based on theme
     * Note: for internal use only
     */
    fill: string,

    /**
     * Used by screen readers to let the user know the the component is in the loading state
     */
    'aria-label': string,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    automationId: undefined,
    inverse: false,
    size: 64,
    theme: undefined,
    fill: '',
    'aria-label': 'Loading',
    styleOverrideReason: undefined
  };

  constructor(props) {
    super(props);
    this.circleSize = 6 + 3 * ((this.props.size - 24) / 40);
  }

  handleRef = ref => {
    this.elem = ref;
  };

  render() {
    const { currentTheme } = getTheme(this.props, this.context);
    const { automationId, size, fill, inverse, ...restProps } = this.props;

    const dotElement = (
      <CircularSpinnerOuter size={size}>
        <CircularSpinnerInner size={size}>
          <SVG
            size={size}
            fill={fill}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${size} ${size}`}
            xmlSpace="preserve"
            theme={currentTheme}
            inverse={inverse}
          >
            <circle
              cx={`${size / 2}`}
              cy={`${size / 2}`}
              r={`${this.circleSize / 2}`}
            />
          </SVG>
        </CircularSpinnerInner>
      </CircularSpinnerOuter>
    );

    return (
      <>
        <Wrapper
          ref={this.handleRef}
          size={size}
          theme={currentTheme}
          inverse={inverse}
          data-automation-id={automationId}
          {...restProps}
        >
          {dotElement}
          {dotElement}
          {dotElement}
          {dotElement}
        </Wrapper>
        <span aria-label={restProps['aria-label']} aria-live="polite" />
      </>
    );
  }
}
